import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { Profile } from '@/components/Profile/Profile';
import { WhatsNew } from '@/components/WhatsNew/WhatsNew';
import Background from '@/images/Background.svg';
import ChatLine from '@/images/ChatLine.svg';
import CloudUpload from '@/images/CloudUpload.svg';
import Presenter from '@/images/Presenter.svg';
import Templates from '@/images/Templates.svg';
import Text from '@/images/Text.svg';
import ThreeSquares from '@/images/ThreeSquares.svg';
import { Header } from '@/pages/AdvancedEditor/components/header/Header';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { SIDEBAR_PANELS } from '@/pages/AdvancedEditor/types';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import { ButtonBase } from '@mui/material';
import { useEffect, useState } from 'react';
import { AvatarsPanel } from '../panels/avatars/AvatarsPanel';
import { BackgroundPanel } from '../panels/background/BackgroundPanel';
import { MediaPanel } from '../panels/media';
import { ScriptPanel } from '../panels/script/ScriptPanel';
import { ShapesPanel } from '../panels/shapes/ShapesPanel';
import { TemplatesPanel } from '../panels/templates/TemplatesPanel';
import { TextPanel } from '../panels/text/TextPanel';

const SidebarWrapper = styled.div`
    display: flex;
    flex-direction: column;

    background-color: #fff;
    max-width: calc(82px + 440px);
    height: 100%;
    z-index: 1;
    border: ${({ theme }) => `1px solid ${theme.colors.black[300]}`};
`;

const PanelsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    height: calc(100% - 72px);
`;

const StaticSidePanel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 82px;
    gap: 16px;
    padding-bottom: 30px;
`;

const DynamicSidePanel = styled.div<{ open: boolean }>`
    display: flex;
    flex-direction: column;
    width: 440px;
    height: 100%;
    position: relative;

    @media (max-width: 1080px) {
        width: 352px;
    }

    transition: width 0.3s;
    ${({ open }) =>
        !open &&
        `
    width: 0;
  
    @media (max-width: 1080px) {
      width: 0;
    }
  `}
`;

const DynamicSidePanelContent = styled.div`
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-gutter: stable;
`;

const SectionButton = styled(ButtonBase, { shouldForwardProp: prop => prop !== 'active' })<{ active?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    width: 64px;
    height: 64px;
    border-radius: 8px;
    padding: 8px;

    font-size: 10px;
    font-weight: 600;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.secondary[700]};
    letter-spacing: -0.2px;

    :hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }

    svg {
        background-color: ${({ theme }) => theme.colors.secondary[700]};
    }

    ${({ active, theme }) =>
        !!active &&
        `
    background-color: ${theme.colors.black[100]};
    color: ${theme.colors.secondary[900]};
    
    svg {
      background-color: ${theme.colors.secondary[900]};
    }
  
  `}
`;

export const Sidebar = ({ backRoute }: { backRoute?: AppRoutes }) => {
    const { store } = useAdvancedEditorContext();
    const [isProfileOpen, toggleProfileOpen] = useState(false);
    const { sidebarOpen, setSidebarOpen, activePanel, setActivePanel } = useAdvancedEditorContext();
    const templatesEnabled = useFlag('template_gallery_v2', false);

    // no selected elements when changing page
    useEffect(() => {
        store.selectElements([]);
    }, [store.activePage]);

    const handlePanelChange = (panel: SIDEBAR_PANELS) => () => {
        setSidebarOpen(true);
        setActivePanel(panel);
        store.selectElements([]);
    };

    return (
        <SidebarWrapper>
            <Header isExpanded={sidebarOpen} backRoute={backRoute} />

            <PanelsWrapper>
                <StaticSidePanel>
                    {templatesEnabled && (
                        <SectionButton
                            key={SIDEBAR_PANELS.TEMPLATES}
                            data-testid="editor-sidebar-templates-button"
                            onClick={handlePanelChange(SIDEBAR_PANELS.TEMPLATES)}
                            active={activePanel === SIDEBAR_PANELS.TEMPLATES}>
                            <MaskedIcon src={Templates} size="20px" />
                            Templates
                        </SectionButton>
                    )}

                    <SectionButton
                        key={SIDEBAR_PANELS.AVATARS}
                        data-testid="editor-sidebar-avatar-button"
                        onClick={handlePanelChange(SIDEBAR_PANELS.AVATARS)}
                        active={activePanel === SIDEBAR_PANELS.AVATARS}>
                        <MaskedIcon src={Presenter} size="20px" />
                        Avatar
                    </SectionButton>

                    <SectionButton
                        key={SIDEBAR_PANELS.SCRIPT}
                        data-testid="editor-sidebar-script-button"
                        onClick={handlePanelChange(SIDEBAR_PANELS.SCRIPT)}
                        active={activePanel === SIDEBAR_PANELS.SCRIPT}>
                        <MaskedIcon src={ChatLine} size="20px" />
                        Script
                    </SectionButton>

                    <SectionButton
                        key={SIDEBAR_PANELS.BACKGROUND}
                        data-testid="editor-sidebar-background-button"
                        onClick={handlePanelChange(SIDEBAR_PANELS.BACKGROUND)}
                        active={activePanel === SIDEBAR_PANELS.BACKGROUND}>
                        <MaskedIcon src={Background} size="20px" />
                        Background
                    </SectionButton>

                    <SectionButton
                        key={SIDEBAR_PANELS.TEXT}
                        data-testid="editor-sidebar-text-button"
                        onClick={handlePanelChange(SIDEBAR_PANELS.TEXT)}
                        active={activePanel === SIDEBAR_PANELS.TEXT}>
                        <MaskedIcon src={Text} size="20px" />
                        Text
                    </SectionButton>

                    <SectionButton
                        key={SIDEBAR_PANELS.SHAPES}
                        data-testid="editor-sidebar-shape-button"
                        onClick={handlePanelChange(SIDEBAR_PANELS.SHAPES)}
                        active={activePanel === SIDEBAR_PANELS.SHAPES}>
                        <MaskedIcon src={ThreeSquares} size="20px" />
                        Shapes
                    </SectionButton>

                    <SectionButton
                        key={SIDEBAR_PANELS.MEDIA}
                        data-testid="editor-sidebar-media-button"
                        onClick={handlePanelChange(SIDEBAR_PANELS.MEDIA)}
                        active={activePanel === SIDEBAR_PANELS.MEDIA}>
                        <MaskedIcon src={CloudUpload} size="24px" />
                        Media
                    </SectionButton>

                    <WhatsNew sidebarWidth="82px" hideDetails iconColor={theme.colors.secondary[700]} />
                    <Profile isOpen={isProfileOpen} toggleOpen={toggleProfileOpen} sidebarWidth="82px" hideDetails />
                </StaticSidePanel>

                <DynamicSidePanel open={sidebarOpen}>
                    <DynamicSidePanelContent>
                        {activePanel === SIDEBAR_PANELS.TEMPLATES && <TemplatesPanel />}
                        {activePanel === SIDEBAR_PANELS.AVATARS && <AvatarsPanel />}
                        {activePanel === SIDEBAR_PANELS.SCRIPT && <ScriptPanel />}
                        {activePanel === SIDEBAR_PANELS.BACKGROUND && <BackgroundPanel />}
                        {activePanel === SIDEBAR_PANELS.TEXT && <TextPanel />}
                        {activePanel === SIDEBAR_PANELS.SHAPES && <ShapesPanel />}
                        {activePanel === SIDEBAR_PANELS.MEDIA && <MediaPanel />}
                    </DynamicSidePanelContent>
                </DynamicSidePanel>
            </PanelsWrapper>
        </SidebarWrapper>
    );
};
